import React, { useEffect, useRef } from "react";
import { MicLevelBars } from "./MicLevelBars";
import { MIC_LIST_FOOTER, MIC_LIST_TITLE } from "./micCopy";
import type { MicDevice } from "./micDevices";

export interface MicListProps {
    inputs: MicDevice[];
    selectedId: string | null;
    probeStream: MediaStream | null;
    onPick: (d: MicDevice) => void;
    onClose: () => void;
}

const MUTED = "rgba(255,255,255,0.7)";

export const MicList = ({ inputs, selectedId, probeStream, onPick, onClose }: MicListProps) => {
    const rowRefs = useRef<(HTMLLIElement | null)[]>([]);

    const focusRow = (i: number) => {
        const row = rowRefs.current[i];
        row?.focus();
        row?.scrollIntoView?.({ block: "nearest" });
    };

    // Open with the selected row visible even when the list scrolls.
    useEffect(() => {
        const i = inputs.findIndex((d) => d.deviceId === selectedId);
        if (i > 0) rowRefs.current[i]?.scrollIntoView?.({ block: "nearest" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onKeyDown = (e: React.KeyboardEvent, index: number, d: MicDevice) => {
        switch (e.key) {
            case "Enter":
            case " ":
                e.preventDefault();
                onPick(d);
                break;
            case "Escape":
                e.preventDefault();
                onClose();
                break;
            case "ArrowDown":
                e.preventDefault();
                focusRow(Math.min(index + 1, inputs.length - 1));
                break;
            case "ArrowUp":
                e.preventDefault();
                focusRow(Math.max(index - 1, 0));
                break;
        }
    };

    return (
        <div className="rounded-lg bg-[#181F2A]">
            <button
                type="button"
                aria-expanded="true"
                onClick={onClose}
                className="flex min-h-[40px] w-full items-center gap-[10px] border-0 border-b border-white/10 bg-transparent pl-4 pr-3 text-left text-[13px] font-semibold leading-[18px] text-white [@media(pointer:coarse)]:min-h-[44px]"
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={MUTED}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M19 10v2a7 7 0 0 1-14 0v-2M12 19v3M8 22h8M12 15a3 3 0 0 1-3-3V5a3 3 0 0 1 6 0v7a3 3 0 0 1-3 3z" />
                </svg>
                <span className="flex-1">{MIC_LIST_TITLE}</span>
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={MUTED}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            <ul
                role="listbox"
                aria-label="Microphones"
                className="m-0 max-h-[max(96px,min(320px,calc(100vh-330px)))] list-none overflow-y-auto py-1 pl-0"
            >
                {inputs.map((d, i) => {
                    const selected = d.deviceId === selectedId;
                    return (
                        <li
                            key={d.deviceId}
                            ref={(el) => {
                                rowRefs.current[i] = el;
                            }}
                            role="option"
                            aria-selected={selected}
                            tabIndex={selected || (selectedId === null && i === 0) ? 0 : -1}
                            onClick={() => onPick(d)}
                            onKeyDown={(e) => onKeyDown(e, i, d)}
                            className={`flex min-h-[24px] cursor-pointer items-center gap-[10px] px-4 py-[10px] text-[14px] text-white hover:bg-white/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-[#2ACBD1] [@media(pointer:coarse)]:min-h-[44px] ${selected ? "bg-[rgba(46,222,229,0.08)]" : ""}`}
                        >
                            {selected ? (
                                <svg
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="#2ACBD1"
                                    strokeWidth="2.5"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                >
                                    <path d="M20 6L9 17l-5-5" />
                                </svg>
                            ) : (
                                <span className="w-4" />
                            )}
                            <span className="flex min-w-0 flex-1 items-baseline gap-1">
                                <span className="line-clamp-2 min-w-0 break-words" title={d.label}>
                                    {d.label}
                                </span>
                                {d.builtIn && (
                                    <span className="flex-none whitespace-nowrap text-[12px] text-white/70">
                                        · built in
                                    </span>
                                )}
                            </span>
                            {selected && <MicLevelBars stream={probeStream} />}
                        </li>
                    );
                })}
            </ul>
            <div className="border-0 border-t border-white/10 px-4 pb-3 pt-2 text-[12px] leading-4 text-white/70">
                {MIC_LIST_FOOTER}
            </div>
        </div>
    );
};
